import React, { useState, useEffect, useCallback } from "react";
import { Field, FormikErrors, useFormikContext } from "formik";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import BuildIcon from "@mui/icons-material/Build";
import ConstructionIcon from "@mui/icons-material/Construction";
import CarRepairIcon from "@mui/icons-material/CarRepair";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { BaseExpenseFieldsProps, ExpenseEntry, ExpenseFormData } from "./types";
import {
  garageApi,
  mechanicsApi,
  Garage,
  Mechanic,
} from "../../../services/api";
import { formatInputCurrency } from "../../../utils/utils";
import { masterDataAllocationApi } from "../../../project/project_module/services/apiService";
import { useParams } from "react-router-dom";
import {
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import { Button, Checkbox } from "@mui/material";
import { v4 as uuidv4 } from "uuid";

const SERVICE_TYPES = [
  "Regular Service",
  "Oil Change",
  "Tyre Change",
  "Battery Replacement",
  "AC Service",
  "Brake Service",
  "Engine Repair",
  "Transmission Repair",
  "Electrical Repair",
  "Body Work",
  "Paint Work",
  "Suspension Repair",
  "Clutch Repair",
  "Radiator Service",
  "Other",
] as const;

const MAINTENANCE_ITEMS = [
  // Engine & Performance
  "Engine Oil",
  "Oil Filter",
  "Air Filter",
  "Fuel Filter",
  "Spark Plugs",
  "Timing Belt",
  "Timing Chain",
  "Water Pump",
  "Radiator",
  "Thermostat",
  "Fuel Pump",
  "Fuel Injectors",
  "Carburetor",

  // Transmission & Drivetrain
  "Transmission Oil",
  "Clutch Plate",
  "Clutch Cable",
  "Drive Belt",
  "CV Joint",
  "Differential Oil",

  // Braking System
  "Brake Pads",
  "Brake Shoes",
  "Brake Disc",
  "Brake Fluid",
  "Brake Cable",
  "Master Cylinder",

  // Suspension & Steering
  "Shock Absorbers",
  "Struts",
  "Springs",
  "Steering Wheel",
  "Power Steering Fluid",
  "Ball Joints",

  // Electrical
  "Battery",
  "Alternator",
  "Starter Motor",
  "Headlights",
  "Tail Lights",
  "Indicators",
  "Wiring Harness",
  "Fuses",

  // Tyres & Wheels
  "Tyres",
  "Wheel Alignment",
  "Wheel Balancing",
  "Rim",

  // AC & Cooling
  "AC Compressor",
  "AC Filter",
  "AC Gas",
  "Coolant",

  // Body & Exterior
  "Windshield",
  "Side Mirror",
  "Door Handle",
  "Bumper",
  "Fender",

  // Other
  "Wipers",
  "Horn",
  "Seat Covers",
  "Floor Mats",
  "Custom",
] as const;

const ACTION_TYPES = [
  "Repaired",
  "Replaced",
  "Serviced",
  "Inspected",
  "Adjusted",
  "Cleaned",
] as const;

const WARRANTY_PERIODS = [
  "No Warranty",
  "1 Month",
  "3 Months",
  "6 Months",
  "1 Year",
  "2 Years",
  "3 Years",
  "Lifetime",
  "Custom",
] as const;

const SERVICE_PROVIDER_TYPES = ["Garage", "Mechanic"] as const;

// Mapping service types to relevant maintenance items
const SERVICE_TYPE_TO_ITEMS: Record<string, string[]> = {
  "Regular Service": [
    "Engine Oil",
    "Oil Filter",
    "Air Filter",
    "Fuel Filter",
    "Spark Plugs",
    "Brake Fluid",
    "Coolant",
    "Battery",
    "Wipers",
    "Custom",
  ],
  "Oil Change": [
    "Engine Oil",
    "Oil Filter",
    "Transmission Oil",
    "Differential Oil",
    "Custom",
  ],
  "Tyre Change": [
    "Tyres",
    "Wheel Alignment",
    "Wheel Balancing",
    "Rim",
    "Custom",
  ],
  "Battery Replacement": [
    "Battery",
    "Alternator",
    "Starter Motor",
    "Wiring Harness",
    "Fuses",
    "Custom",
  ],
  "AC Service": ["AC Compressor", "AC Filter", "AC Gas", "Coolant", "Custom"],
  "Brake Service": [
    "Brake Pads",
    "Brake Shoes",
    "Brake Disc",
    "Brake Fluid",
    "Brake Cable",
    "Master Cylinder",
    "Custom",
  ],
  "Engine Repair": [
    "Engine Oil",
    "Oil Filter",
    "Air Filter",
    "Fuel Filter",
    "Spark Plugs",
    "Timing Belt",
    "Timing Chain",
    "Water Pump",
    "Radiator",
    "Thermostat",
    "Fuel Pump",
    "Fuel Injectors",
    "Carburetor",
    "Custom",
  ],
  "Transmission Repair": [
    "Transmission Oil",
    "Clutch Plate",
    "Clutch Cable",
    "Drive Belt",
    "CV Joint",
    "Differential Oil",
    "Custom",
  ],
  "Electrical Repair": [
    "Battery",
    "Alternator",
    "Starter Motor",
    "Headlights",
    "Tail Lights",
    "Indicators",
    "Wiring Harness",
    "Fuses",
    "Custom",
  ],
  "Body Work": [
    "Windshield",
    "Side Mirror",
    "Door Handle",
    "Bumper",
    "Fender",
    "Seat Covers",
    "Floor Mats",
    "Custom",
  ],
  "Paint Work": ["Bumper", "Fender", "Custom"],
  "Suspension Repair": [
    "Shock Absorbers",
    "Struts",
    "Springs",
    "Ball Joints",
    "Custom",
  ],
  "Clutch Repair": ["Clutch Plate", "Clutch Cable", "Custom"],
  "Radiator Service": [
    "Radiator",
    "Thermostat",
    "Coolant",
    "Water Pump",
    "Custom",
  ],
  Other: MAINTENANCE_ITEMS.slice(), // All items for "Other" service type
};

// Debounce hook for search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function MaintenanceExpenseFields({
  index,
  isProjectExpense = false,
  isTripExpense = false,
}: BaseExpenseFieldsProps) {
  const { projectId } = useParams();
  const { values, setFieldValue, errors } = useFormikContext<ExpenseFormData>();
  const expenseErrors = errors.expenses?.[index] as
    | FormikErrors<ExpenseEntry>
    | undefined;
  const [showWarrantyFields, setShowWarrantyFields] = useState<boolean>(false);
  const [showServiceProviderDetails, setShowServiceProviderDetails] =
    useState<boolean>(false);
  const [showAdditionalDetails, setShowAdditionalDetails] =
    useState<boolean>(false);

  // Service provider selection state
  const [serviceProviderType, setServiceProviderType] =
    useState<string>("Garage");
  const [serviceProviders, setServiceProviders] = useState<
    (Garage | Mechanic)[]
  >([]);
  const [selectedServiceProvider, setSelectedServiceProvider] = useState<
    Garage | Mechanic | null
  >(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string>("");
  const [isManualEntry, setIsManualEntry] = useState<boolean>(false);

  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Get current values
  const currentServiceType = values.expenses[index]?.serviceType || "";
  const currentMaintenanceItem = values.expenses[index]?.maintenanceItem || "";
  const currentActionType = values.expenses[index]?.actionType || "";
  const currentWarrantyPeriod = values.expenses[index]?.warrantyPeriod || "";

  // Get filtered maintenance items based on service type
  const getFilteredMaintenanceItems = (serviceType: string) => {
    return SERVICE_TYPE_TO_ITEMS[serviceType] || MAINTENANCE_ITEMS;
  };

  // Handle service type change
  const handleServiceTypeChange = (serviceType: string) => {
    setFieldValue(`expenses.${index}.isEdited`, true);
    setFieldValue(`expenses.${index}.serviceType`, serviceType);

    // Clear maintenance item if it's not valid for the new service type
    const validItems = getFilteredMaintenanceItems(serviceType);
    if (
      currentMaintenanceItem &&
      !validItems.includes(currentMaintenanceItem)
    ) {
      setFieldValue(`expenses.${index}.maintenanceItem`, "");
    }
  };

  // Handle action type change to show/hide warranty fields
  const handleActionTypeChange = (actionType: string) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setFieldValue(`expenses.${index}.actionType`, actionType);

    if (actionType === "Replaced") {
      setShowWarrantyFields(true);
    } else {
      setShowWarrantyFields(false);
      // Clear warranty fields when not replacing
      setFieldValue(`expenses.${index}.warrantyPeriod`, "");
      setFieldValue(`expenses.${index}.warrantyStartDate`, "");
      setFieldValue(`expenses.${index}.warrantyEndDate`, "");
      setFieldValue(`expenses.${index}.customWarrantyPeriod`, "");
    }
  };

  // Calculate warranty end date
  const calculateWarrantyEndDate = (startDate: string, period: string) => {
    if (
      !startDate ||
      period === "No Warranty" ||
      period === "Custom" ||
      period === "Lifetime"
    ) {
      return "";
    }

    const start = new Date(startDate);
    let endDate = new Date(start);

    switch (period) {
      case "1 Month":
        endDate.setMonth(start.getMonth() + 1);
        break;
      case "3 Months":
        endDate.setMonth(start.getMonth() + 3);
        break;
      case "6 Months":
        endDate.setMonth(start.getMonth() + 6);
        break;
      case "1 Year":
        endDate.setFullYear(start.getFullYear() + 1);
        break;
      case "2 Years":
        endDate.setFullYear(start.getFullYear() + 2);
        break;
      case "3 Years":
        endDate.setFullYear(start.getFullYear() + 3);
        break;
    }

    return endDate.toISOString().split("T")[0];
  };

  // Handle warranty period change
  const handleWarrantyPeriodChange = (period: string) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setFieldValue(`expenses.${index}.warrantyPeriod`, period);

    if (period !== "Custom") {
      setFieldValue(`expenses.${index}.customWarrantyPeriod`, "");
    }

    // Auto-calculate warranty end date if start date exists
    const startDate = values.expenses[index]?.warrantyStartDate;
    if (startDate) {
      const endDate = calculateWarrantyEndDate(startDate, period);
      setFieldValue(`expenses.${index}.warrantyEndDate`, endDate);
    }
  };

  // Handle warranty start date change
  const handleWarrantyStartDateChange = (startDate: string) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setFieldValue(`expenses.${index}.warrantyStartDate`, startDate);

    // Auto-calculate warranty end date if period is selected
    const period = values.expenses[index]?.warrantyPeriod;
    if (period) {
      const endDate = calculateWarrantyEndDate(startDate, period);
      setFieldValue(`expenses.${index}.warrantyEndDate`, endDate);
    }
  };

  // Search service providers based on type
  const searchServiceProviders = useCallback(
    async (term: string, providerType: string) => {
      if (term.length < 2 && term.length > 0) return;

      setIsLoading(true);
      setSearchError("");

      try {
        let data: (Garage | Mechanic)[] = [];
        switch (providerType) {
          case "Garage":
            data = await garageApi.search(term);
            break;
          case "Mechanic":
            data = await mechanicsApi.search(term);
            break;
          default:
            data = [];
        }
        setServiceProviders(data);
      } catch (error) {
        setSearchError(`Failed to load ${providerType.toLowerCase()} data`);
        setServiceProviders([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const searchProjectServiceProviders = useCallback(
    async (term: string, providerType: string) => {
      if (term.length < 2 && term.length > 0) return;

      setIsLoading(true);
      setSearchError("");

      try {
        let data: any[] = [];
        switch (providerType) {
          case "Garage":
            data =
              await masterDataAllocationApi.getMasterDataAllocationsByProject(
                projectId!,
                "garage"
              );
            break;
          case "Mechanic":
            data =
              await masterDataAllocationApi.getMasterDataAllocationsByProject(
                projectId!,
                "mechanic"
              );
            break;
          default:
            data = [];
        }
        setServiceProviders(
          data?.map((provider) => provider.masterData) as (Garage | Mechanic)[]
        );
      } catch (error) {
        setSearchError(`Failed to load ${providerType.toLowerCase()} data`);
        setServiceProviders([]);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Effect to search when debounced term or provider type changes
  useEffect(() => {
    if (isProjectExpense) {
      searchProjectServiceProviders(debouncedSearchTerm, serviceProviderType);
    } else {
      searchServiceProviders(debouncedSearchTerm, serviceProviderType);
    }
  }, [
    debouncedSearchTerm,
    serviceProviderType,
    searchServiceProviders,
    searchProjectServiceProviders,
    isProjectExpense,
  ]);

  // Initialize state from form values (for edit mode)
  useEffect(() => {
    const expense = values.expenses[index];
    if (expense) {
      // Initialize service provider type
      if (expense.serviceProviderType) {
        setServiceProviderType(expense.serviceProviderType);
      }

      // Initialize warranty fields visibility
      if (expense.actionType === "Replaced") {
        setShowWarrantyFields(true);
      }

      // Initialize manual entry mode if serviceProviderId is empty but serviceProvider is filled
      if (!expense.serviceProviderId && expense.serviceProvider) {
        setIsManualEntry(true);
      }

      // Try to find and set selected service provider if serviceProviderId exists
      if (expense.serviceProviderId && serviceProviders.length > 0) {
        const provider = serviceProviders.find(
          (p) => p.id === expense.serviceProviderId
        );
        if (provider) {
          setFieldValue(`expenses.${index}.serviceProviderId`, provider.id);
          setFieldValue(`expenses.${index}.serviceProvider`, provider.name);
          setFieldValue(`expenses.${index}.paidTo`, provider.name);
          setSelectedServiceProvider(provider);
          setIsManualEntry(false);
        }
      }
    }
  }, [values.expenses, index, serviceProviders]);

  // Handle service provider type change
  const handleServiceProviderTypeChange = (type: string) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setServiceProviderType(type);
    setSelectedServiceProvider(null);
    setIsManualEntry(false);
    setSearchTerm("");
    // Set service provider type in form and clear other fields
    setFieldValue(`expenses.${index}.serviceProviderType`, type);
    setFieldValue(`expenses.${index}.isManualEntry`, false);
    setFieldValue(`expenses.${index}.serviceProviderId`, "");
    setFieldValue(`expenses.${index}.serviceProvider`, "");
    setFieldValue(`expenses.${index}.paidTo`, ""); // Fix: Clear paidTo field
    setFieldValue(`expenses.${index}.contactNumber`, "");
    setFieldValue(`expenses.${index}.location`, "");
  };

  // Handle service provider selection
  const handleServiceProviderChange = (
    provider: Garage | Mechanic | null,
    inputValue?: string
  ) => {
    //set isEdited to true
    setFieldValue(`expenses.${index}.isEdited`, true);
    setSelectedServiceProvider(provider);

    if (provider) {
      // Auto-fill fields from master data
      setIsManualEntry(false);
      setFieldValue(`expenses.${index}.isManualEntry`, false);
      setFieldValue(`expenses.${index}.serviceProviderId`, provider.id);
      setFieldValue(`expenses.${index}.serviceProvider`, provider.name);
      setFieldValue(`expenses.${index}.paidTo`, provider.name); // Fix: Set paidTo field
      setFieldValue(
        `expenses.${index}.contactNumber`,
        "contactNumber" in provider
          ? provider.contactNumber
          : "phoneNumber" in provider
          ? provider.phoneNumber
          : ""
      );
      setFieldValue(`expenses.${index}.location`, provider.location);
    } else if (inputValue && inputValue.trim() !== "") {
      // Manual entry mode - generate UUID and set manual entry flag
      setIsManualEntry(true);
      const newUUID = uuidv4();
      setFieldValue(`expenses.${index}.isManualEntry`, true);
      setFieldValue(`expenses.${index}.serviceProviderId`, newUUID);
      setFieldValue(`expenses.${index}.serviceProvider`, inputValue.trim());
      setFieldValue(`expenses.${index}.paidTo`, inputValue.trim()); // Fix: Set paidTo field
    } else {
      // Clear fields
      setIsManualEntry(false);
      setFieldValue(`expenses.${index}.isManualEntry`, false);
      setFieldValue(`expenses.${index}.serviceProviderId`, "");
      setFieldValue(`expenses.${index}.serviceProvider`, "");
      setFieldValue(`expenses.${index}.paidTo`, ""); // Fix: Clear paidTo field
      setFieldValue(`expenses.${index}.contactNumber`, "");
      setFieldValue(`expenses.${index}.location`, "");
    }
  };

  // Get option label for autocomplete
  const getOptionLabel = (option: string | Garage | Mechanic) => {
    if (typeof option === "string") return option;
    const contact =
      "contactNumber" in option
        ? option.contactNumber
        : "phoneNumber" in option
        ? option.phoneNumber
        : "";
    return `${option.name} - ${contact}`;
  };

  // Render service provider option
  const renderServiceProviderOption = (
    props: any,
    option: Garage | Mechanic
  ) => (
    <Box component="li" {...props} sx={{ fontSize: "0.75rem", py: 0.5 }}>
      <Box sx={{ width: "100%" }}>
        <Typography
          variant="subtitle2"
          sx={{ fontSize: "0.75rem", fontWeight: 500 }}
        >
          {option.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontSize: "0.75rem", mt: 0.25 }}
        >
          {option.location}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.75rem", display: "block", mt: 0.25 }}
        >
          Contact:{" "}
          {"contactNumber" in option
            ? option.contactNumber
            : "phoneNumber" in option
            ? option.phoneNumber
            : "N/A"}
        </Typography>
        {serviceProviderType === "Garage" && "contactName" in option && (
          <Chip
            label={`Contact: ${option.contactName}`}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mt: 0.5, fontSize: "0.75rem" }}
          />
        )}
      </Box>
    </Box>
  );

  const handleMarkAsPaid = useCallback(
    (isPaid: boolean) => {
      if (isPaid) {
        setFieldValue(`expenses.${index}.payments`, [
          ...values.expenses[index].payments!,
          {
            type: "ADVANCE",
            mode: "CASH",
            date: values.expenses[index].expenseDate || "",
            location: values.expenses[index].location || "",
            receivedFrom: values.expenses[index].paidTo || "",
            remarks: "",
            status: "PAID",
            isEdited: false,
            isDeleted: false,
            isNew: true,
            amount: values.expenses[index].amount,
            masterExpenseId: values.expenses[index].id || null,
          },
        ]);
      } else {
        setFieldValue(`expenses.${index}.payments`, []);
      }
    },
    [index, setFieldValue, values.expenses]
  );

  return (
    <Grid container item xs={12} spacing={1}>
      {!isTripExpense && (
        <Grid item xs={12} sm={2}>
          <Field
            as={TextField}
            label="Date"
            type="date"
            name={`expenses.${index}.expenseDate`}
            fullWidth
            required
            {...commonDateFieldProps}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={2}>
        <Field
          as={TextField}
          select
          label="Service Type"
          name={`expenses.${index}.serviceType`}
          fullWidth
          required
          value={currentServiceType}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleServiceTypeChange(e.target.value)
          }
          error={Boolean(expenseErrors?.serviceType)}
          helperText={expenseErrors?.serviceType}
          {...commonTextFieldProps}
        >
          {SERVICE_TYPES.map((type) => (
            <MenuItem key={type} value={type} sx={{ fontSize: "0.75rem" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  py: 0.25,
                }}
              >
                {type.includes("Repair") ? (
                  <ConstructionIcon
                    fontSize="small"
                    sx={{ width: "0.75rem", height: "0.75rem" }}
                  />
                ) : type.includes("Service") ? (
                  <BuildIcon
                    fontSize="small"
                    sx={{ width: "0.75rem", height: "0.75rem" }}
                  />
                ) : (
                  <CarRepairIcon
                    fontSize="small"
                    sx={{ width: "0.75rem", height: "0.75rem" }}
                  />
                )}
                {type}
              </Box>
            </MenuItem>
          ))}
        </Field>
      </Grid>

      <Grid item xs={12} sm={2}>
        <Field
          as={TextField}
          select
          label="Maintenance Item"
          name={`expenses.${index}.maintenanceItem`}
          fullWidth
          required
          value={currentMaintenanceItem}
          error={Boolean(expenseErrors?.maintenanceItem)}
          helperText={
            expenseErrors?.maintenanceItem ||
            (currentServiceType
              ? `Items available for ${currentServiceType}`
              : "Please select a service type first")
          }
          disabled={!currentServiceType}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.isEdited`, true);
            setFieldValue(`expenses.${index}.maintenanceItem`, e.target.value);
          }}
          {...commonTextFieldProps}
        >
          {getFilteredMaintenanceItems(currentServiceType).map((item) => (
            <MenuItem sx={{ fontSize: "0.75rem" }} key={item} value={item}>
              {item}
            </MenuItem>
          ))}
        </Field>
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          select
          label="Action Performed"
          name={`expenses.${index}.actionType`}
          fullWidth
          required
          value={currentActionType}
          error={Boolean(expenseErrors?.actionType)}
          helperText={expenseErrors?.actionType}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleActionTypeChange(e.target.value)
          }
          {...commonTextFieldProps}
        >
          {ACTION_TYPES.map((action) => (
            <MenuItem sx={{ fontSize: "0.75rem" }} key={action} value={action}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {action}
                {action === "Replaced" && (
                  <Chip
                    label="May have warranty"
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{
                      fontSize: "0.55rem",
                      borderRadius: "5px",
                      p: 0,
                      lineHeight: "1.2",
                      height: "20px",
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Field>
      </Grid>

      {currentMaintenanceItem === "Custom" && (
        <Grid item xs={3}>
          <Field
            as={TextField}
            label="Custom Maintenance Item"
            name={`expenses.${index}.customMaintenanceItem`}
            fullWidth
            required
            placeholder="Enter the specific item or service..."
            helperText="Specify the exact maintenance item or service performed"
            {...commonTextFieldProps}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          select
          label="Service Provider Type"
          name={`expenses.${index}.serviceProviderType`}
          value={
            values.expenses[index]?.serviceProviderType || serviceProviderType
          }
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleServiceProviderTypeChange(e.target.value)
          }
          fullWidth
          required
          {...commonTextFieldProps}
        >
          {SERVICE_PROVIDER_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  py: 0.25,
                }}
              >
                {type === "Garage" ? (
                  <BusinessIcon sx={{ fontSize: "1rem" }} />
                ) : (
                  <PersonIcon sx={{ fontSize: "1rem" }} />
                )}
                {type}
              </Box>
            </MenuItem>
          ))}
        </Field>
      </Grid>

      <Grid item xs={12} sm={3}>
        <Autocomplete
          freeSolo
          options={serviceProviders}
          loading={isLoading}
          getOptionLabel={getOptionLabel}
          value={
            isManualEntry
              ? values.expenses[index].paidTo
              : selectedServiceProvider
          }
          onChange={(_, newValue, reason) => {
            if (reason === "selectOption" && typeof newValue === "object") {
              // Selected from dropdown - reset manual entry mode
              setIsManualEntry(false);
              setFieldValue(`expenses.${index}.isManualEntry`, false);
              handleServiceProviderChange(newValue);
            } else if (reason === "clear") {
              // Clear selection - reset all fields
              setIsManualEntry(false);
              setFieldValue(`expenses.${index}.isManualEntry`, false);
              handleServiceProviderChange(null);
            }
          }}
          onInputChange={(_, inputValue, reason) => {
            setSearchTerm(inputValue);

            if (reason === "input") {
              const matchingOption = serviceProviders.find((option) =>
                getOptionLabel(option)
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              );

              if (!matchingOption && inputValue.trim() !== "") {
                // Manual entry - generate UUID and set manual entry flag
                setIsManualEntry(true);
                const newUUID = uuidv4();
                setFieldValue(`expenses.${index}.isEdited`, true);
                setFieldValue(`expenses.${index}.isManualEntry`, true);
                setFieldValue(`expenses.${index}.serviceProviderId`, newUUID);
                setFieldValue(
                  `expenses.${index}.serviceProvider`,
                  inputValue.trim()
                );
                setFieldValue(`expenses.${index}.paidTo`, inputValue.trim());
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={`Select ${serviceProviderType}`}
              placeholder={`Search from master data or enter manually...`}
              required
              fullWidth
              error={!!searchError || !!expenseErrors?.paidTo}
              helperText={
                searchError
                  ? searchError
                  : expenseErrors?.paidTo
                  ? "Service provider name is required"
                  : `Select from dropdown or type to add new ${serviceProviderType.toLowerCase()}`
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              {...commonTextFieldProps}
            />
          )}
          renderOption={renderServiceProviderOption}
          noOptionsText={
            isLoading
              ? "Searching..."
              : searchError
              ? "Error loading options"
              : `No matching ${serviceProviderType.toLowerCase()} found. You can still type to add manually.`
          }
        />
      </Grid>

      {/* Manual Entry Alert */}
      {/* {isManualEntry && (
        <Grid item xs={12}>
          <Alert severity="info" sx={{ mb: 1, fontSize: "0.75rem" }}>
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              <strong>Manual Entry Mode:</strong> This{" "}
              {serviceProviderType.toLowerCase()} is not in our master data.
              Please fill in the details manually below.
            </Typography>
          </Alert>
        </Grid>
      )} */}

      {/* Manual Entry Fields */}
      {isManualEntry && (
        <>
          <Grid item xs={12} sm={3}>
            <Field
              as={TextField}
              label="Contact Number"
              name={`expenses.${index}.contactNumber`}
              fullWidth
              placeholder="Enter contact number"
              {...commonTextFieldProps}
              inputProps={{
                maxLength: 10,
              }}
              error={Boolean(
                expenseErrors?.contactNumber && expenseErrors?.contactNumber
              )}
              helperText={
                expenseErrors?.contactNumber && expenseErrors?.contactNumber
              }
              sx={{
                "& .MuiInputBase-root": { fontSize: "0.75rem" },
                "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={3}>
            <Field
              as={TextField}
              label="Location"
              name={`expenses.${index}.location`}
              fullWidth
              placeholder="Enter location"
              {...commonTextFieldProps}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={3}>
        <Field name={`expenses.${index}.amount`}>
          {({ field, meta }: any) => (
            <TextField
              {...field}
              {...commonTextFieldProps}
              label="Amount"
              fullWidth
              required
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error ? meta.error : ""}
              value={field.value ? formatInputCurrency(field.value) : ""}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, "");
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: value,
                    },
                  });
                  setFieldValue(`expenses.${index}.isEdited`, true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    component="span"
                    sx={{ mr: 0.5, fontSize: "0.75rem" }}
                  >
                    ₹
                  </Typography>
                ),
              }}
              inputProps={{
                inputMode: "decimal",
              }}
            />
          )}
        </Field>
      </Grid>

      {/* Auto-filled Service Provider Details */}
      {selectedServiceProvider &&
        !isManualEntry &&
        showServiceProviderDetails && (
          <Grid item xs={12}>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "primary.light",
                bgcolor: "#F0F7FF",
                mb: 1.5,
              }}
            >
              <Typography variant="subtitle2" color="primary.dark" gutterBottom>
                {serviceProviderType} Details (From Master Data)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Name: {selectedServiceProvider.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Contact:{" "}
                    {"contactNumber" in selectedServiceProvider
                      ? selectedServiceProvider.contactNumber
                      : "phoneNumber" in selectedServiceProvider
                      ? selectedServiceProvider.phoneNumber
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Location: {selectedServiceProvider.location}
                  </Typography>
                </Grid>
                {serviceProviderType === "Garage" &&
                  "contactName" in selectedServiceProvider && (
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Contact Person: {selectedServiceProvider.contactName}
                      </Typography>
                    </Grid>
                  )}
              </Grid>
            </Box>
          </Grid>
        )}

      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => setShowAdditionalDetails(!showAdditionalDetails)}
          size={smallButtonProp.size}
          sx={{
            ...smallButtonProp.sx,
            color: "grey",
            borderColor: "grey.400",
            "&:hover": {
              borderColor: "grey.400",
              color: "grey",
            },
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
            {showAdditionalDetails
              ? "Hide Additional Details"
              : "Add Additional Details"}
          </Typography>
        </Button>
        {Number(values.expenses[index].amount) > 0 &&
          values.expenses[index].payments?.length === 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    values.expenses[index].payments?.length! &&
                    values.expenses[index].payments?.length! > 0
                      ? true
                      : false
                  }
                  onChange={(e) => {
                    handleMarkAsPaid(e.target.checked);
                  }}
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: "0.7rem" },
                  }}
                />
              }
              label="Mark as Paid"
              sx={{
                p: 0,
                pr: 1,
                ml: 0.75,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.7rem",
                  color: "primary.main",
                },
              }}
            />
          )}
      </Grid>

      {showAdditionalDetails && (
        <>
          {/* GST Section: Calculate and display GST amounts */}
          <Grid item xs={12}>
            <Box
              sx={{
                p: 1.5,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Typography
                variant="subtitle2"
                color="primary.main"
                sx={{ fontSize: "0.75rem", mb: 1 }}
              >
                GST Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    type="number"
                    as={TextField}
                    label="GST %"
                    name={`expenses.${index}.gst`}
                    fullWidth
                    placeholder="Enter GST %"
                    inputProps={{
                      min: "0",
                      max: "100",
                      step: "0.01",
                    }}
                    {...commonTextFieldProps}
                  />
                </Grid>

                {Boolean(values.expenses[index]?.gst) &&
                  Number(values.expenses[index].gst) > 0 && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="GST Amount"
                          value={`₹ ${(
                            (Number(values.expenses[index].amount || 0) *
                              Number(values.expenses[index].gst)) /
                            100
                          ).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}`}
                          fullWidth
                          InputProps={{ readOnly: true }}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="CGST Amount"
                          value={`₹ ${(
                            (Number(values.expenses[index].amount || 0) *
                              Number(values.expenses[index].gst)) /
                            200
                          ).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })} (${(
                            Number(values.expenses[index].gst) / 2
                          ).toFixed(2)}%)`}
                          fullWidth
                          InputProps={{ readOnly: true }}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="SGST Amount"
                          value={`₹ ${(
                            (Number(values.expenses[index].amount || 0) *
                              Number(values.expenses[index].gst)) /
                            200
                          ).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })} (${(
                            Number(values.expenses[index].gst) / 2
                          ).toFixed(2)}%)`}
                          fullWidth
                          InputProps={{ readOnly: true }}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Total Amount (Inc. GST)"
                          value={`₹ ${(
                            Number(values.expenses[index].amount || 0) *
                            (1 + Number(values.expenses[index].gst) / 100)
                          ).toLocaleString("en-IN", {
                            maximumFractionDigits: 2,
                            minimumFractionDigits: 2,
                          })}`}
                          fullWidth
                          InputProps={{ readOnly: true }}
                          {...commonTextFieldProps}
                        />
                      </Grid>
                    </>
                  )}
              </Grid>
            </Box>
          </Grid>

          {currentActionType === "Replaced" && (
            <>
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 1,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                    mb: 1.5,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="primary.main"
                    sx={{ fontSize: "0.75rem", mb: 0.5 }}
                  >
                    Warranty Information (Part Replacement)
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: "0.75rem", mb: 1 }}
                  >
                    Since this item was replaced, please provide warranty
                    details if applicable.
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        label="Warranty Start Date"
                        type="date"
                        name={`expenses.${index}.warrantyStartDate`}
                        fullWidth
                        helperText="Date when warranty coverage begins"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleWarrantyStartDateChange(e.target.value)
                        }
                        {...commonDateFieldProps}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Field
                        as={TextField}
                        select
                        label="Warranty Period"
                        name={`expenses.${index}.warrantyPeriod`}
                        fullWidth
                        value={currentWarrantyPeriod}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          handleWarrantyPeriodChange(e.target.value)
                        }
                        {...commonTextFieldProps}
                      >
                        {WARRANTY_PERIODS.map((period) => (
                          <MenuItem
                            key={period}
                            value={period}
                            sx={{ fontSize: "0.75rem" }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                py: 0.25,
                              }}
                            >
                              {period === "Lifetime" && (
                                <Chip
                                  label="Best"
                                  size="small"
                                  color="success"
                                  sx={{ fontSize: "0.75rem" }}
                                />
                              )}
                              {period === "No Warranty" && (
                                <Chip
                                  label="Risk"
                                  size="small"
                                  color="warning"
                                  sx={{ fontSize: "0.75rem" }}
                                />
                              )}
                              {period}
                            </Box>
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>

                    {currentWarrantyPeriod === "Custom" && (
                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label="Custom Warranty Period"
                          name={`expenses.${index}.customWarrantyPeriod`}
                          fullWidth
                          required
                          placeholder="e.g., 18 months, 2.5 years, etc."
                          helperText="Specify the exact warranty period"
                          {...commonTextFieldProps}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setFieldValue(`expenses.${index}.isEdited`, true);
                            setFieldValue(
                              `expenses.${index}.customWarrantyPeriod`,
                              e.target.value
                            );
                          }}
                        />
                      </Grid>
                    )}

                    {currentWarrantyPeriod !== "No Warranty" &&
                      currentWarrantyPeriod !== "Custom" && (
                        <Grid item xs={12} sm={6}>
                          <Field
                            as={TextField}
                            label="Warranty End Date"
                            type="date"
                            name={`expenses.${index}.warrantyEndDate`}
                            fullWidth
                            helperText="Auto-calculated based on warranty period"
                            InputProps={{
                              readOnly: currentWarrantyPeriod !== "Custom",
                            }}
                            {...commonDateFieldProps}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setFieldValue(`expenses.${index}.isEdited`, true);
                              setFieldValue(
                                `expenses.${index}.warrantyEndDate`,
                                e.target.value
                              );
                            }}
                          />
                        </Grid>
                      )}
                  </Grid>
                </Box>
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <Field
              as={TextField}
              label="Odometer Reading (km)"
              name={`expenses.${index}.odometerReading`}
              type="number"
              fullWidth
              inputProps={{
                min: "0",
                step: "1",
              }}
              helperText="Vehicle mileage at time of service"
              {...commonTextFieldProps}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(`expenses.${index}.isEdited`, true);
                setFieldValue(
                  `expenses.${index}.odometerReading`,
                  e.target.value
                );
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field
              as={TextField}
              label="Next Service Due (km)"
              name={`expenses.${index}.nextServiceDue`}
              type="number"
              fullWidth
              inputProps={{
                min: "0",
                step: "1",
              }}
              helperText="When next service is recommended"
              {...commonTextFieldProps}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(`expenses.${index}.isEdited`, true);
                setFieldValue(
                  `expenses.${index}.nextServiceDue`,
                  e.target.value
                );
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field
              as={TextField}
              label="Invoice/Bill Number"
              name={`expenses.${index}.invoiceNumber`}
              fullWidth
              placeholder="Enter bill/invoice number"
              helperText="For record keeping and warranty claims"
              type="number"
              inputProps={{
                min: "0",
                step: "1",
              }}
              {...commonTextFieldProps}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(`expenses.${index}.isEdited`, true);
                setFieldValue(
                  `expenses.${index}.invoiceNumber`,
                  e.target.value
                );
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Field
              as={TextField}
              label="Work Order Number"
              name={`expenses.${index}.workOrderNumber`}
              fullWidth
              placeholder="Enter work order number"
              helperText="Service center reference number"
              type="number"
              inputProps={{
                min: "0",
                step: "1",
              }}
              {...commonTextFieldProps}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(`expenses.${index}.isEdited`, true);
                setFieldValue(
                  `expenses.${index}.workOrderNumber`,
                  e.target.value
                );
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Field
              as={TextField}
              label="Notes"
              name={`expenses.${index}.notes`}
              fullWidth
              multiline
              rows={2}
              placeholder="Any additional notes about this maintenance/repair..."
              {...commonTextFieldProps}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue(`expenses.${index}.isEdited`, true);
                setFieldValue(`expenses.${index}.notes`, e.target.value);
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
}

export default MaintenanceExpenseFields;
